import type { IconName } from "@/components/Icon/iconMap";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { useState } from "react";

import TooltipButton from "@/components/Button/TooltipButton";
import { ConfirmActionDialog } from "@/components/Dialog/ConfirmActionDialog";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { match } from "ts-pattern";
import useAppUserCreate from "../../hooks/useAppUserCreate";
import useAppUserDeactivate from "../../hooks/useAppUserDeactivate";
import useAppUserReactivate from "../../hooks/useAppUserReactivate";
import type { AppUserAction } from "../../types/action";
import { suggestedAction } from "../../types/action";

export type AppUserActionProps = {
  appIntegrationId: string;
  appUserId?: string;
  personId?: string;
  appName: string;
  action?: AppUserAction;
  onSuccess?: () => void;
};

/** AppUserのRuleMatchに合わせてDressCodeが操作者に推奨するアクションを実行するためのボタンコンポーネント */
export const AppUserActionButton = ({
  appIntegrationId,
  appUserId,
  personId,
  appName,
  action,
  onSuccess,
}: AppUserActionProps) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "domain.appUser",
  });
  const tc = useCommonTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const createAppUser = useAppUserCreate({
    appIntegrationId,
    personId: personId || "",
    onSuccess,
  });

  const reactivateAppUser = useAppUserReactivate({
    appIntegrationId,
    appUserId: appUserId || "",
    onSuccess,
  });

  const deactivateAppUser = useAppUserDeactivate({
    appIntegrationId,
    appUserId: appUserId || "",
    onSuccess,
  });

  const isLoading =
    createAppUser.isLoading ||
    reactivateAppUser.isLoading ||
    deactivateAppUser.isLoading;

  const getButtonProps = () =>
    match(action?.type)
      .with(suggestedAction.CREATE, () => ({
        icon: "UserPlusIcon" as const satisfies IconName,
        text: t("component.AppUserActionButton.createUser"),
        tooltipKey: "component.AppUserActionButton.toolTipCreateUserOnWebPage",
        dialogTitle: {
          resource: t("appUser"),
          action: tc("action.create"),
        },
        onClick: () => {
          createAppUser.mutate();
        },
      }))
      .with(suggestedAction.REACTIVATE, () => ({
        icon: "UserPlusIcon" as const satisfies IconName,
        text: t("component.AppUserActionButton.reactivateUser"),
        tooltipKey:
          "component.AppUserActionButton.toolTipReactivateUserOnWebPage",
        dialogTitle: {
          resource: t("appUser"),
          action: tc("action.reactivate"),
        },
        onClick: () => {
          reactivateAppUser.mutate();
        },
      }))
      .with(suggestedAction.DEACTIVATE, () => ({
        icon: "UserRoundMinusIcon" as const satisfies IconName,
        text: t("component.AppUserActionButton.deactivateUser"),
        tooltipKey:
          "component.AppUserActionButton.toolTipDeactivateUserOnWebPage",
        dialogTitle: {
          resource: t("appUser"),
          action: tc("action.deactivate"),
        },
        onClick: () => setIsDialogOpen(true),
      }))
      .otherwise(() => null);

  const buttonProps = getButtonProps();
  if (!buttonProps) return null;

  const handleAction = () => {
    deactivateAppUser.mutate();
    setIsDialogOpen(false);
  };

  const getDescription = (action: AppUserAction): string => {
    if ("describe" in action) {
      return action.describe;
    }
    return t("component.AppUserActionButton.describe");
  };

  if (!action) {
    return <div>{t("component.AppUserActionButton.noRecommendedAction")}</div>;
  }

  return (
    <>
      <TooltipButton
        icon={buttonProps.icon}
        variant="outline"
        iconColor="primary"
        isLoading={isLoading}
        onClick={buttonProps.onClick}
        disabled={action.disabled || isLoading}
        content={
          action.disabled
            ? t(buttonProps.tooltipKey, {
                defaultValue: "",
                name: appName,
              })
            : undefined
        }
      >
        {buttonProps.text}
      </TooltipButton>
      <ConfirmActionDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleAction}
        title={buttonProps.dialogTitle}
        description={getDescription(action)}
      />
    </>
  );
};
